import AbcIcon from '@mui/icons-material/Abc';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Editor } from '@tiptap/core';
import { useState, useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ActiveMarks } from '../../../../../interfaces/ActiveMarks';

interface ReportToolbarProps {
  editor: Editor | null;
  activeMarks: ActiveMarks;
  setMarks: React.Dispatch<React.SetStateAction<ActiveMarks>>;
}

const ReportToolbar = ({
  editor,
  activeMarks,
  setMarks,
}: ReportToolbarProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorHeadingEl, setAnchorHeadingEl] = useState<null | HTMLElement>(
    null,
  );
  const openFontSizeMenu = Boolean(anchorEl);
  const openHeadingMenu = Boolean(anchorHeadingEl);
  const intl = useIntl();

  const toolTipMessages = useMemo(
    () => ({
      formatting: intl.formatMessage({
        defaultMessage: 'Erase text formatting',
      }),
      bold: intl.formatMessage({ defaultMessage: 'Bold (Ctrl/Cmd + B)' }),
      underline: intl.formatMessage({
        defaultMessage: 'Underline (Ctrl/Cmd + U)',
      }),
      italic: intl.formatMessage({ defaultMessage: 'Italic (Ctrl/Cmd + I)' }),
      bulletList: intl.formatMessage({ defaultMessage: 'Unordered List' }),
      numberedList: intl.formatMessage({ defaultMessage: 'Ordered List' }),
      fontSize: intl.formatMessage({ defaultMessage: 'Font Size' }),
      heading: intl.formatMessage({ defaultMessage: 'Headings' }),
    }),
    [intl],
  );

  const fontSizes = useMemo(
    () => ({
      small: '12',
      normal: '16',
      medium: '18',
      large: '24',
    }),
    [],
  );

  const headingSizes = {
    one: '28px',
    two: '21px',
    three: '16px',
    four: '14px',
    five: '12px',
    six: '9px',
  };

  const handleToggleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.bold = editor.isActive('bold');
        return newMarks;
      });
    }
  }, [editor, setMarks]);

  const handleToggleUnderline = useCallback(() => {
    editor?.chain().focus().toggleUnderline().run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.underline = editor.isActive('underline');
        return newMarks;
      });
    }
  }, [editor, setMarks]);

  const handleToggleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.italic = editor.isActive('italic');
        return newMarks;
      });
    }
  }, [editor, setMarks]);

  const handleToggleBulletList = useCallback(() => {
    editor?.chain().focus().toggleBulletList().run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.bulletList = editor.isActive('bulletList');
        return newMarks;
      });
    }
  }, [editor, setMarks]);

  const handleToggleNumberedList = useCallback(() => {
    editor?.chain().focus().toggleOrderedList().run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.numberedList = editor.isActive('orderedList');
        return newMarks;
      });
    }
  }, [editor, setMarks]);

  const handleToggleNormalFontSize = () => {
    editor?.chain().focus().unsetFontSize().run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.textStyle = false;
        return newMarks;
      });
    }
  };

  const handleToggleMediumFontSize = () => {
    editor?.chain().focus().setFontSize(fontSizes.medium).run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.textStyle = true;
        return newMarks;
      });
    }
  };

  const handleToggleLargeFontSize = () => {
    editor?.chain().focus().setFontSize(fontSizes.large).run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.textStyle = true;
        return newMarks;
      });
    }
  };

  const handleToggleSmallFontSize = () => {
    editor?.chain().focus().setFontSize(fontSizes.small).run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.textStyle = true;
        return newMarks;
      });
    }
  };

  // Due to Typescript issues with getting Level type from TipTap and level cant be used with number, each heading toggle must be a separate function
  const handleToggleHeadingOne = () => {
    editor?.chain().focus().toggleHeading({ level: 1 }).run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.heading = editor.isActive('heading');
        return newMarks;
      });
    }
  };

  const handleToggleHeadingTwo = () => {
    editor?.chain().focus().toggleHeading({ level: 2 }).run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.heading = editor.isActive('heading');
        return newMarks;
      });
    }
  };
  const handleToggleHeadingThree = () => {
    editor?.chain().focus().toggleHeading({ level: 3 }).run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.heading = editor.isActive('heading');
        return newMarks;
      });
    }
  };
  const handleToggleHeadingFour = () => {
    editor?.chain().focus().toggleHeading({ level: 4 }).run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.heading = editor.isActive('heading');
        return newMarks;
      });
    }
  };
  const handleToggleHeadingFive = () => {
    editor?.chain().focus().toggleHeading({ level: 5 }).run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.heading = editor.isActive('heading');
        return newMarks;
      });
    }
  };
  const handleToggleHeadingSix = () => {
    editor?.chain().focus().toggleHeading({ level: 6 }).run();
    if (editor) {
      setMarks((oldMarks) => {
        const newMarks = { ...oldMarks };
        newMarks.heading = editor.isActive('heading');
        return newMarks;
      });
    }
  };

  const handleHeadingButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorHeadingEl(event.currentTarget);
  };

  const handleFontSizeButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseHeading = () => {
    setAnchorHeadingEl(null);
  };

  const handleCloseFontSize = () => {
    setAnchorEl(null);
  };

  const checkFormatting = useCallback(() => {
    const formattingValues: boolean[] = Object.values(activeMarks);
    return formattingValues.some((value) => value);
  }, [activeMarks]);

  const resetMarks = () => {
    setMarks({
      bold: false,
      italic: false,
      underline: false,
      bulletList: false,
      numberedList: false,
      heading: false,
      textStyle: false,
    });
  };

  const HeadingMenu = () => (
    <Menu
      anchorEl={anchorHeadingEl}
      id='heading-menu'
      open={openHeadingMenu}
      onClick={handleCloseHeading}
      onClose={handleCloseHeading}
      transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      slotProps={{
        paper: {
          sx: {
            width: '180px',
          },
        },
      }}
    >
      <MenuItem onClick={handleToggleHeadingOne}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.one} fontWeight={'bold'}>
            <FormattedMessage defaultMessage='Heading 1' />
          </Typography>
          {editor?.isActive('heading', {
            level: 1,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingTwo}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.two} fontWeight={'bold'}>
            <FormattedMessage defaultMessage='Heading 2' />
          </Typography>
          {editor?.isActive('heading', {
            level: 2,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingThree}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.three}>
            <FormattedMessage defaultMessage='Heading 3' />
          </Typography>
          {editor?.isActive('heading', {
            level: 3,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingFour}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.four}>
            <FormattedMessage defaultMessage='Heading 4' />
          </Typography>
          {editor?.isActive('heading', {
            level: 4,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingFive}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.five}>
            <FormattedMessage defaultMessage='Heading 5' />
          </Typography>
          {editor?.isActive('heading', {
            level: 5,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleHeadingSix}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={headingSizes.six}>
            <FormattedMessage defaultMessage='Heading 6' />
          </Typography>
          {editor?.isActive('heading', {
            level: 6,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
    </Menu>
  );

  const FontSizeMenu = () => (
    <Menu
      anchorEl={anchorEl}
      id='font-size-menu'
      open={openFontSizeMenu}
      onClick={handleCloseFontSize}
      onClose={handleCloseFontSize}
      transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      slotProps={{
        paper: {
          sx: {
            width: '140px',
          },
        },
      }}
    >
      <MenuItem onClick={handleToggleSmallFontSize}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={fontSizes.small + 'px'}>
            <FormattedMessage defaultMessage='Small' />
          </Typography>
          {editor?.isActive('textStyle', {
            fontSize: fontSizes.small,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleNormalFontSize}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <FormattedMessage defaultMessage='Normal' />
          {!editor?.isActive('textStyle') && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleMediumFontSize}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={fontSizes.medium + 'px'}>
            <FormattedMessage defaultMessage='Medium' />
          </Typography>
          {editor?.isActive('textStyle', {
            fontSize: fontSizes.medium,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
      <MenuItem onClick={handleToggleLargeFontSize}>
        <Box
          display='flex'
          justifyContent='space-between'
          width='100%'
          alignItems='center'
        >
          <Typography fontSize={fontSizes.large + 'px'}>
            <FormattedMessage defaultMessage='Large' />
          </Typography>
          {editor?.isActive('textStyle', {
            fontSize: fontSizes.large,
          }) && <CheckIcon />}
        </Box>
      </MenuItem>
    </Menu>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        padding: 1,
        alignItems: 'center',
      }}
    >
      <HeadingMenu />
      <FontSizeMenu />
      {editor && (
        <>
          <>
            <Tooltip title={toolTipMessages.formatting}>
              <ToggleButton
                value='toggleFormatting'
                sx={{
                  border: 0,
                  '&:disabled': {
                    border: 0,
                  },
                }}
                color={editor ? 'teal' : 'lightGray'}
                selected={checkFormatting()}
                onChange={() => {
                  editor
                    .chain()
                    .selectAll()
                    .unsetAllMarks()
                    .unsetFontSize()
                    .clearNodes()
                    .focus()
                    .run();
                  resetMarks();
                }}
              >
                <AbcIcon />
              </ToggleButton>
            </Tooltip>
            <Divider
              flexItem
              orientation='vertical'
              sx={{
                my: 1,
                mx: 1,
              }}
            />
          </>
          <>
            <>
              <Tooltip title={toolTipMessages.heading}>
                <ToggleButton
                  value='heading'
                  sx={{
                    border: 0,
                    mr: 0.5,
                  }}
                  color='teal'
                  onClick={handleHeadingButtonClick}
                  selected={editor.isActive('heading')}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontWeight: 500,
                      fontSize: fontSizes.medium + 'px',
                    }}
                  >
                    H+
                  </Typography>
                  <ArrowDropDownIcon />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={toolTipMessages.fontSize}>
                <ToggleButton
                  value='fontSize'
                  sx={{
                    border: 0,
                    mr: 0.5,
                  }}
                  color='teal'
                  onClick={handleFontSizeButtonClick}
                  selected={editor.isActive('textStyle')}
                >
                  <FormatSizeIcon />
                  <ArrowDropDownIcon />
                </ToggleButton>
              </Tooltip>
              <Divider
                flexItem
                orientation='vertical'
                sx={{
                  my: 1,
                  mx: 1,
                }}
              />
            </>
            <>
              <Tooltip title={toolTipMessages.bold}>
                <ToggleButton
                  value='bold'
                  sx={{
                    border: 0,
                    mr: 0.5,
                  }}
                  color='teal'
                  onClick={handleToggleBold}
                  selected={editor.isActive('bold') || activeMarks.bold}
                >
                  <FormatBoldIcon />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={toolTipMessages.underline}>
                <ToggleButton
                  value='underline'
                  sx={{
                    border: 0,
                    mr: 0.5,
                  }}
                  color='teal'
                  selected={editor.isActive('underline')}
                  onClick={handleToggleUnderline}
                >
                  <FormatUnderlinedIcon />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={toolTipMessages.italic}>
                <ToggleButton
                  value='italic'
                  sx={{
                    border: 0,
                    mr: 0.5,
                  }}
                  color='teal'
                  selected={editor.isActive('italic')}
                  onClick={handleToggleItalic}
                >
                  <FormatItalicIcon />
                </ToggleButton>
              </Tooltip>
            </>
            <>
              <Divider
                flexItem
                orientation='vertical'
                sx={{
                  my: 1,
                  mx: 1,
                }}
              />
              <Tooltip title={toolTipMessages.bulletList}>
                <ToggleButton
                  value='bulletList'
                  sx={{
                    border: 0,
                    mr: 0.5,
                  }}
                  color='teal'
                  selected={editor.isActive('bulletList')}
                  onClick={handleToggleBulletList}
                >
                  <FormatListBulletedIcon />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={toolTipMessages.numberedList}>
                <ToggleButton
                  value='numberedList'
                  sx={{
                    border: 0,
                    mr: 0.5,
                  }}
                  color='teal'
                  selected={editor.isActive('orderedList')}
                  onClick={handleToggleNumberedList}
                >
                  <FormatListNumberedIcon />
                </ToggleButton>
              </Tooltip>
            </>
          </>
          <Divider
            flexItem
            orientation='vertical'
            sx={{
              my: 1,
              mx: 1,
            }}
          />
        </>
      )}
    </Box>
  );
};

export default ReportToolbar;
